import { createFileRoute } from "@tanstack/react-router";
import { CTAButtons } from "@/components/site/CTAButtons";
import { MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import serviceAreasAssetUrl from "@/assets/service-areas-cleaner.webp";
const serviceAreasAsset = { url: serviceAreasAssetUrl };


export const Route = createFileRoute("/service-areas")({
  head: () => ({
    meta: [
      { title: "Cleaning Service Areas — Tidworth, Andover, Salisbury | IZHUB" },
      { name: "description", content: "Local cleaners covering Tidworth, Bulford, Larkhill, Amesbury, Ludgershall, Andover and Salisbury — within about 20 miles on Salisbury Plain." },
      { property: "og:title", content: "Cleaning Service Areas — Salisbury Plain | IZHUB" },
      { property: "og:description", content: "See exactly where IZHUB Cleaning covers across Tidworth, Andover, Salisbury and Salisbury Plain." },
      { property: "og:url", content: "https://izhubcleaning.uk/service-areas" },
    ],
    links: [{ rel: "canonical", href: "https://izhubcleaning.uk/service-areas" }],
  }),
  component: Areas,
});

const areas = [
  { name: "Tidworth", note: "Regular domestic, march-out and end-of-tenancy cleaning across Tidworth SFA and civilian addresses." },
  { name: "Bulford", note: "March-out specialists for Bulford SFA — trusted by service families across the garrison." },
  { name: "Larkhill", note: "Domestic, deep and inspection-standard cleans across Larkhill and surrounding villages." },
  { name: "Amesbury", note: "Domestic, deep, Airbnb and end-of-tenancy cleaning throughout Amesbury and nearby." },
  { name: "Ludgershall", note: "Reliable local cleaning for homes and businesses across Ludgershall." },
  { name: "Andover", note: "Domestic, deep and end-of-tenancy cleaning for Andover households and landlords." },
  { name: "Salisbury", note: "Full service coverage across Salisbury — homes, offices, short-lets and more." },
  { name: "Salisbury Plain", note: "Cleaning across the wider Salisbury Plain area and surrounding villages." },
];

// OpenStreetMap embed centred on Tidworth (bbox ~20 mile radius across Salisbury Plain)
const MAP_SRC =
  "https://www.openstreetmap.org/export/embed.html?bbox=-2.15%2C50.98%2C-1.18%2C51.34&layer=mapnik&marker=51.160456%2C-1.6642579";

function Areas() {
  return (
    <>
      <section className="border-b border-border bg-cream/85 backdrop-blur-sm">
        <div className="container-x py-16 md:py-24">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Local cleaners across <span className="rainbow-text">Salisbury Plain</span>.</h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl">We're proud to serve homes, landlords, businesses and military families across the towns below. Based in Tidworth, we cover roughly a 20-mile radius across Wiltshire and Hampshire.</p>
              <div className="mt-8"><CTAButtons /></div>
            </div>
            <div>
              <img
                src={serviceAreasAsset.url}
                alt="IZHUB cleaning professional holding supplies"
                loading="lazy"
                className="w-full h-auto object-contain rounded-3xl shadow-soft"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Interactive coverage map */}
      <section className="pt-4 pb-12 md:pb-16">
        <div className="container-x">
          <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-stretch">
            <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <iframe
                title="IZHUB Cleaning coverage map — Tidworth, Andover and Salisbury"
                src={MAP_SRC}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="block w-full h-[360px] md:h-[460px]"
              />
              <a
                href="https://www.openstreetmap.org/?mlat=51.160456&mlon=-1.6642579#map=11/51.16/-1.66"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-3 right-3 rounded-full bg-background/95 backdrop-blur px-3 py-1.5 text-xs font-medium border border-border shadow-soft"
              >
                View larger map ↗
              </a>
            </div>

            <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
              <span className="eyebrow">Coverage at a glance</span>
              <h2 className="mt-3 font-display text-2xl">Where we clean</h2>
              <ul className="mt-5 space-y-3 text-sm">
                <li className="flex gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>Standard coverage: <strong>within 20 miles of Tidworth (SP9)</strong> — no travel fee.</span>
                </li>
                <li className="flex gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>Postcodes we regularly serve: <strong>SP1–SP11, SN8, SN9, SP10, SP11</strong>.</span>
                </li>
                <li className="flex gap-2.5">
                  <AlertCircle className="h-4 w-4 text-sage shrink-0 mt-0.5" />
                  <span>Further afield (20–30 miles): possible with a small travel surcharge — just ask.</span>
                </li>
                <li className="flex gap-2.5">
                  <AlertCircle className="h-4 w-4 text-sage shrink-0 mt-0.5" />
                  <span>Outside 30 miles of Tidworth: we can't currently cover — we'll happily point you to a trusted local team where possible.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20">
        <div className="container-x grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((a) => (
            <div key={a.name} className="rounded-3xl border border-border bg-card p-6">
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-sage-soft text-primary"><MapPin className="h-4 w-4" /></span>
                <h2 className="font-display text-xl">Cleaners in {a.name}</h2>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">{a.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x max-w-3xl">
          <div className="text-surface">
          <h2 className="font-display text-2xl md:text-3xl">Common local searches</h2>
          <p className="mt-4 text-muted-foreground">If you've searched for any of the following, you're in the right place:</p>
          <ul className="mt-5 grid gap-2 sm:grid-cols-2 text-sm text-muted-foreground list-disc pl-5">
            <li>Cleaning services in Tidworth</li>
            <li>Domestic cleaning in Tidworth</li>
            <li>End of tenancy cleaning in Andover</li>
            <li>Deep cleaning in Salisbury</li>
            <li>Airbnb cleaning in Wiltshire</li>
            <li>March-out cleaning near Salisbury Plain</li>
            <li>Cleaners in Amesbury</li>
            <li>Cleaners in Ludgershall</li>
          </ul>
          </div>
        </div>
      </section>
    </>
  );
}
